import Button from "@mui/material/Button";
import { useTheme } from '@mui/material/styles';

export default function MaterialUiButton(props) {
  const theme = useTheme();
  const { palette = {} } = theme;
  const { error = {}, primary = {}, success = {}, typography = {} } = palette;

  // Brand colors
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";
  const greenDark = "#7a9c4a";
  const navyDark = "#032d42";

  const baseStyles = {
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: 600,
    fontFamily: '"Lato", sans-serif',
    fontSize: "0.875rem",
    padding: "10px 20px",
    transition: "all 0.2s ease-in-out",
    boxShadow: "none",
    "&:focus-visible": {
      outline: `2px solid ${greenColor}`,
      outlineOffset: "2px",
    },
  };

  const cssClasses = {
    cancel: {
      ...baseStyles,
      color: "#ffffff",
      backgroundColor: navyColor,
      border: `2px solid ${navyColor}`,
      "&:hover": {
        backgroundColor: "transparent",
        color: navyColor,
        boxShadow: "none",
      },
    },
    delete: {
      ...baseStyles,
      backgroundColor: error.main || "#ef4444",
      color: "#ffffff",
      border: `2px solid ${error.main || "#ef4444"}`,
      "&:hover": {
        backgroundColor: "transparent",
        color: error.main || "#ef4444",
        boxShadow: "none",
      },
    },
    disabled: {
      ...baseStyles,
      backgroundColor: "#e2e8f0",
      color: "#94a3b8",
      border: "2px solid #e2e8f0",
      cursor: "not-allowed",
      "&:hover": {
        backgroundColor: "#e2e8f0",
        color: "#94a3b8",
        boxShadow: "none",
      },
    },
    submit: {
      ...baseStyles,
      backgroundColor: greenColor,
      color: "#ffffff",
      border: `2px solid ${greenColor}`,
      "&:hover": {
        backgroundColor: greenDark,
        borderColor: greenDark,
        boxShadow: "0 4px 12px rgba(160, 196, 109, 0.3)",
      },
    },
    primary: {
      ...baseStyles,
      backgroundColor: navyColor,
      color: "#ffffff",
      border: `2px solid ${navyColor}`,
      "&:hover": {
        backgroundColor: navyDark,
        borderColor: navyDark,
        boxShadow: "0 4px 12px rgba(0, 40, 67, 0.3)",
      },
    },
    secondary: {
      ...baseStyles,
      backgroundColor: "transparent",
      color: navyColor,
      border: `2px solid #e2e8f0`,
      "&:hover": {
        backgroundColor: "#f8fafc",
        borderColor: navyColor,
        boxShadow: "none",
      },
    },
    text: {
      ...baseStyles,
      backgroundColor: "transparent",
      color: navyColor,
      border: "2px solid transparent",
      padding: "8px 16px",
      "&:hover": {
        backgroundColor: "rgba(160, 196, 109, 0.1)",
        boxShadow: "none",
      },
    },
  };

  const {
    className = {},
    color = "primary",
    cypressId,
    disabled = false,
    disableElevation = true,
    disableFocusRipple,
    disableRipple,
    endIcon,
    fullWidth = false,
    id,
    label = "Submit",
    onClick,
    onMouseDown = () => { },
    role,
    size = "medium",
    startIcon,
    sx = {},
    type,
    variant = "contained",
  } = props;

  // Determine which style to apply
  const getButtonStyles = () => {
    if (disabled) return cssClasses.disabled;
    
    switch (color) {
      case "cancel":
        return cssClasses.cancel;
      case "delete":
        return cssClasses.delete;
      case "submit":
        return cssClasses.submit;
      case "secondary":
        return cssClasses.secondary;
      case "text":
        return cssClasses.text;
      case "primary":
      default:
        return cssClasses.primary;
    }
  };

  // Size adjustments
  const sizeStyles = {
    small: {
      padding: "6px 14px",
      fontSize: "0.8125rem",
    },
    medium: {
      padding: "10px 20px",
      fontSize: "0.875rem",
    },
    large: {
      padding: "12px 28px",
      fontSize: "1rem",
    },
  };

  return (
    <Button
      sx={{
        ...getButtonStyles(),
        ...(sizeStyles[size] || sizeStyles.medium),
        ...(className || {}),
        ...sx,
      }}
      color="inherit"
      data-cypress-id={cypressId}
      disabled={disabled || color === "disabled"}
      disableElevation={disableElevation}
      disableFocusRipple={disableFocusRipple}
      disableRipple={disableRipple}
      endIcon={endIcon}
      fullWidth={fullWidth}
      id={id || null}
      onClick={onClick}
      onMouseDown={onMouseDown}
      role={role}
      size={size}
      startIcon={startIcon}
      type={type}
      variant={variant}
    >
      {label}
    </Button>
  );
}
